import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUserStats } from '../context/UserStatsContext';

const UserProfileIcon: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { stats, loading } = useUserStats();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getInitials = (email: string | null | undefined): string => {
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold flex items-center justify-center hover:from-blue-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
        title="View profile statistics"
      >
        {getInitials(currentUser?.email)}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50">
          {/* User email */}
          <div className="px-4 pb-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-800 truncate">
              {currentUser?.email}
            </p>
          </div>

          {/* Profile link */}
          <div className="px-2 py-2 border-b border-gray-100">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/app/profile');
              }}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              View Profile
            </button>
          </div>

          {/* Stats section */}
          {loading ? (
            <div className="px-4 py-6 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : stats ? (
            <div className="px-4 py-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Practice Statistics
              </h4>

              {/* Total practice time */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">
                    {formatMinutes(stats.totalPracticeMinutes)}
                  </p>
                  <p className="text-xs text-gray-500">Total practice time</p>
                </div>
              </div>

              {/* Sessions grid */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xl font-bold text-green-700">
                    {stats.pronunciationSessions}
                  </p>
                  <p className="text-xs text-green-600">Pronunciation sessions</p>
                  {stats.averagePronunciationScore > 0 && (
                    <p className="text-xs text-green-500 mt-1">
                      Avg: {stats.averagePronunciationScore}%
                    </p>
                  )}
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xl font-bold text-purple-700">
                    {stats.interviewSessions}
                  </p>
                  <p className="text-xs text-purple-600">Interview sessions</p>
                  {stats.averageInterviewScore > 0 && (
                    <p className="text-xs text-purple-500 mt-1">
                      Avg: {stats.averageInterviewScore}/10
                    </p>
                  )}
                </div>
              </div>

              {/* Streak */}
              <div className="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
                <div className="text-2xl">🔥</div>
                <div>
                  <p className="text-lg font-bold text-orange-700">
                    {stats.currentStreak} day{stats.currentStreak !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-orange-600">
                    Current streak {stats.longestStreak > stats.currentStreak && `(Best: ${stats.longestStreak})`}
                  </p>
                </div>
              </div>

              {/* Last practice */}
              {stats.lastPracticeDate && (
                <p className="text-xs text-gray-400 mt-3 text-center">
                  Last practice:{' '}
                  {new Date(stats.lastPracticeDate).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <div className="px-4 py-4 text-center text-gray-500 text-sm">
              Start practicing to see your stats!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfileIcon;
